import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Check, ArrowRight } from "lucide-react";
import mockup1 from "../assets/mockup-central.png";
import mockup2 from "../assets/mockup-left.png";
import mockup3 from "../assets/mockup-right.png";

function NotificationCard({ title, line1, line2, className, delay = 0.8 }) {
	return (
		<motion.div
			className={`pointer-events-none absolute z-40 ${className}`}
			initial={{ opacity: 0, y: -16, scale: 0.94 }}
			animate={{ opacity: 1, y: [0, -6, 0], scale: 1 }}
			transition={{
				opacity: { duration: 0.45, delay },
				scale: { duration: 0.45, delay, type: "spring", stiffness: 240, damping: 16 },
				y: { duration: 3.8, repeat: Infinity, ease: "easeInOut", delay: delay + 0.4 },
			}}
		>
			<div className="flex min-w-[168px] items-center gap-2.5 rounded-2xl border border-primary/25 bg-white/95 px-2.5 py-2 shadow-[0_10px_24px_rgba(0,0,0,0.12)]">
				<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-[#2fae83] text-[12px] font-bold text-white">
					{title.charAt(0)}
				</div>
				<div className="min-w-0 flex-1">
					<p className="m-0 truncate text-[11px] font-bold leading-tight text-gray-900">
						{title}
					</p>
					<p className="m-0 truncate text-[10px] leading-tight text-gray-600">
						{line1}
					</p>
					<p className="m-0 truncate text-[10px] leading-tight text-gray-400">
						{line2}
					</p>
				</div>
				<div className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-primary/10">
					<Check className="h-3 w-3 text-primary" strokeWidth={3} />
				</div>
			</div>
		</motion.div>
	);
}

export default function Hero() {
	const { t } = useTranslation();

	const quickBenefits = t("hero.quickBenefits", { returnObjects: true }) || [
		"App + Web incluidas",
		"Sin comisiones",
		"30 días gratis",
	];

	return (
		<section
			id="inicio"
			className="relative overflow-x-hidden bg-gradient-to-br from-primary/5 via-white to-primary/10"
		>
			{/* Decorative elements */}
			<div className="absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
			<div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />

			<div className="relative max-w-7xl mx-auto px-6 lg:px-16 pt-32 md:pt-36 pb-16 md:pb-24">
				{/* Badge */}
				<motion.div
					className="flex justify-center lg:justify-start mb-6 md:mb-6"
					initial={{ opacity: 0, y: -20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5 }}
				>
					<span className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">
						<span className="relative flex h-2 w-2">
							<span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
							<span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
						</span>
						{t("hero.badge")}
					</span>
				</motion.div>

				<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
					{/* Texto */}
					<div className="text-center lg:text-left">
						<motion.h1
							className="text-4xl md:text-5xl lg:text-[3.5rem] font-extrabold text-gray-900 leading-[1.2] tracking-tight"
							initial={{ opacity: 0, y: -30 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.8, ease: "easeOut" }}
							style={{ lineHeight: "1.2" }}
						>
							{t("hero.titlePart1")}{" "}
							<span className="text-primary">{t("hero.titleHighlight")}</span>
						
						</motion.h1>

						<motion.p
							className="mt-6 mb-6 lg:mb-0 text-lg md:text-xl text-gray-600 leading-relaxed max-w-xl mx-auto lg:mx-0"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
						>
							{t("hero.subtitle")}
						</motion.p>

						{/* Mockups - Solo móvil, después de descripción */}
						<div className="flex lg:hidden items-end justify-center relative h-[280px] md:h-[350px] mt-8">
							<motion.img
								src={mockup2}
								alt="App izquierda"
								className="absolute left-1/2 bottom-0 w-40 md:w-48 h-auto drop-shadow-2xl -translate-x-[75%] z-10"
								initial={{ opacity: 0, y: 30 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6 }}
							/>
							<motion.img
								src={mockup1}
								alt="App central"
								className="w-48 md:w-56 h-auto drop-shadow-2xl relative z-20"
								initial={{ opacity: 0, y: 30 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 0.2 }}
							/>
							<motion.img
								src={mockup3}
								alt="App derecha"
								className="absolute right-1/2 bottom-0 w-40 md:w-48 h-auto drop-shadow-2xl translate-x-[75%] z-10"
								initial={{ opacity: 0, y: 30 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 0.4 }}
							/>
							<NotificationCard
								title={t("hero.notifications.reservation.title")}
								line1={t("hero.notifications.reservation.line1")}
								line2={t("hero.notifications.reservation.line2")}
								delay={0.7}
								className="right-0 top-2 md:right-auto md:left-[calc(50%-20px)]"
							/>
							<NotificationCard
								title={t("hero.notifications.order.title")}
								line1={t("hero.notifications.order.line1")}
								line2={t("hero.notifications.order.line2")}
								delay={1}
								className="left-0 bottom-2 md:left-[calc(50%-120px)]"
							/>
						</div>

						{/* Quick Benefits */}
						<motion.div
							className="mt-6 flex flex-wrap justify-center lg:justify-start gap-3"
							initial={{ opacity: 0, y: 15 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.3 }}
						>
							{quickBenefits.map((benefit, index) => (
								<span
									key={index}
									className="inline-flex items-center gap-1.5 text-sm text-gray-700 bg-white px-3 py-1.5 rounded-full shadow-sm border border-gray-100"
								>
									<Check className="w-4 h-4 text-primary" />
									{benefit}
								</span>
							))}
						</motion.div>

						{/* CTAs */}
						<motion.div
							className="mt-8 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
							initial={{ opacity: 0, y: 15 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.7, ease: "easeOut", delay: 0.4 }}
						>
							<a
								href="https://creator.applaby.com"
								target="_blank"
								rel="noreferrer"
								className="group inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 hover:bg-primary/90 transition-all duration-300"
							>
								{t("hero.ctaPrimary")}
								<ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
							</a>
							<a
								href="#sectores"
								onClick={(e) => {
									e.preventDefault();
									document
										.querySelector("#sectores")
										?.scrollIntoView({ behavior: "smooth" });
								}}
								className="inline-flex items-center justify-center gap-2 bg-white text-gray-700 px-8 py-4 rounded-xl font-semibold border-2 border-gray-200 hover:border-primary hover:text-primary transition-all duration-300"
							>
								{t("hero.ctaSecondary")}
							</a>
						</motion.div>

						{/* Trust note */}
						<motion.p
							className="mt-4 text-sm text-gray-500 text-center lg:text-left"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: 0.5, delay: 0.6 }}
						>
							{t("hero.trustNote")}
						</motion.p>

						{/* Features highlight */}
						<motion.div
							className="mt-10 pt-8 border-t border-gray-100 flex flex-wrap justify-center lg:justify-start gap-6"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.7 }}
						>
							{[
								t("hero.features.publish"),
								t("hero.features.stores"),
								t("hero.features.support"),
								t("hero.features.notifications"),
							].map((label) => (
								<div key={label} className="flex items-center gap-2 text-gray-600">
									<div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
										<Check className="w-4 h-4 text-primary" />
									</div>
									<span className="text-sm font-medium">{label}</span>
								</div>
							))}
						</motion.div>
					</div>

					{/* Mockups - Solo escritorio */}
					<div className="relative w-full hidden lg:flex justify-center">
						<div className="relative flex items-end justify-center -space-x-32 xl:-space-x-40">
							<motion.img
								src={mockup2}
								alt="App izquierda"
								className="w-52 xl:w-64 h-auto drop-shadow-2xl -rotate-6 z-20"
								initial={{ opacity: 0, x: -100, y: 30 }}
								animate={{ opacity: 1, x: 0, y: [0, 8, 0], rotate: -6 }}
								transition={{
									opacity: { duration: 0.6, delay: 0.2 },
									x: { duration: 0.6, delay: 0.2 },
									y: { duration: 5, repeat: Infinity, ease: "easeInOut" },
								}}
							/>
							<motion.img
								src={mockup1}
								alt="App central"
								className="w-60 xl:w-72 h-auto drop-shadow-2xl z-30"
								initial={{ opacity: 0, y: 80 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.8, ease: "easeOut" }}
							/>
							<motion.img
								src={mockup3}
								alt="App derecha"
								className="w-52 xl:w-64 h-auto drop-shadow-2xl rotate-6 z-20"
								initial={{ opacity: 0, x: 100, y: 30 }}
								animate={{ opacity: 1, x: 0, y: [0, 8, 0], rotate: 6 }}
								transition={{
									opacity: { duration: 0.6, delay: 0.4 },
									x: { duration: 0.6, delay: 0.4 },
									y: {
										duration: 5,
										repeat: Infinity,
										ease: "easeInOut",
										delay: 0.5,
									},
								}}
							/>
							<NotificationCard
								title={t("hero.notifications.reservation.title")}
								line1={t("hero.notifications.reservation.line1")}
								line2={t("hero.notifications.reservation.line2")}
								delay={0.8}
								className="left-[22%] top-[12%] xl:left-[26%]"
							/>
							<NotificationCard
								title={t("hero.notifications.order.title")}
								line1={t("hero.notifications.order.line1")}
								line2={t("hero.notifications.order.line2")}
								delay={1.15}
								className="-right-4 bottom-[22%] xl:-right-2"
							/>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
